import React, { useState, useEffect, useContext } from "react";
import _ from "lodash";
import { Spin } from "../../common/UIComponents";
import FormInvoice from "../../components/billing/InvoiceForm";
import { saveInvoice } from "../../api/Billing";
import { UserContext } from "../../context/UserContext";

// New estimate screen (Option A): reuse the real Billing Manage component
// (InvoiceForm / BillingForm) so the Estimate tab matches the Billing screen and
// supports the same inline add/edit/LOS editing.
//
// On open we replicate the legacy estimate flow:
//   1. CREATE  — POST /v2/billing (add_order_estimation) to (re)generate the estimate
//   2. DISPLAY — FormInvoice loads order_quotation_invoice_details (the QUOTE) when
//                fromEstimate (see InvoiceForm.getBillingDetails), not invoice_details
//   3. EDIT    — inline add/edit charges + LOS, driven by FormInvoice like Billing Manage
//
// Generate first, then mount FormInvoice so it reads the freshly generated quote.

// Module-level in-flight guard: the estimate TabPane can mount this twice on open
// (parent re-render). Dedupe so the CREATE fires exactly once per open; it resets
// on completion so a later re-open regenerates (matching the old tab).
const inflightCreate = {};

const EstimateBillingDetails = (props) => {
  const orderId = _.get(props, "order.id") || props.order_id;
  const orderGroupType = _.get(props, "order.order_group_type") || "ORDER";
  const templateMethod = _.get(props, "order.billing_template_method");
  const onRefresh =
    typeof props.updateQuote === "function" ? props.updateQuote : () => {};

  const { orgRules } = useContext(UserContext);
  // When Sync Estimation & Billing is ON the estimate IS the billing, so the
  // create is a real billing record (is_for_invoice_quote=false); when OFF the
  // estimate is a separate quote (is_for_invoice_quote=true).
  const syncEstAndBilling =
    _.get(orgRules, "org_billing_rules.sync_est_and_billing") === "true";
  const isForInvoiceQuote = syncEstAndBilling ? "false" : "true";

  const [generating, setGenerating] = useState(true);

  useEffect(() => {
    if (!orderId) {
      setGenerating(false);
      return undefined;
    }
    if (!inflightCreate[orderId]) {
      const current_date = new Date().toISOString().split("T")[0];
      inflightCreate[orderId] = saveInvoice({
        order_id: orderId,
        is_for_invoice_quote: isForInvoiceQuote,
        source: "ESTIMATE",
        current_date,
      }).catch(() => {}); // on failure still show whatever quote exists
    }
    let active = true;
    inflightCreate[orderId].finally(() => {
      delete inflightCreate[orderId]; // allow a fresh generate on the next open
      if (active) setGenerating(false);
    });
    return () => {
      active = false;
    };
    // orderId is the only real dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  if (!orderId) return null;

  return (
    <div className="estimate-billing-manage">
      <style>{`
        /* Keep the embedded billing form inside its column on the order screen.
           Neutralise FormInvoice's own fixed-height vertical scroll (.billing_indv)
           so the order tab is the single vertical scrollbar (was doubled), and
           give charge tables a horizontal scroll so their amounts are never hidden
           under the fixed right-hand details panel. */
        .estimate-billing-manage { width: 100%; max-width: 100%; }
        .estimate-billing-manage .billing_indv {
          max-width: 100%;
          height: auto !important;
          max-height: none !important;
          overflow-y: visible !important;
        }
        .estimate-billing-manage .ant-table-body { overflow-x: auto; }
        /* Slim, subtle horizontal scrollbar on the charge tables (was a thick blue bar). */
        .estimate-billing-manage .ant-table-body::-webkit-scrollbar { height: 6px; }
        .estimate-billing-manage .ant-table-body::-webkit-scrollbar-track { background: transparent; }
        .estimate-billing-manage .ant-table-body::-webkit-scrollbar-thumb {
          background: rgba(0, 0, 0, 0.18);
          border-radius: 3px;
        }
        .estimate-billing-manage .ant-table-body::-webkit-scrollbar-thumb:hover { background: rgba(0, 0, 0, 0.3); }
        .estimate-billing-manage .ant-table-body { scrollbar-width: thin; scrollbar-color: rgba(0,0,0,0.2) transparent; }

        /* Group the header (LoS + actions, then zone + Total) into one clean card
           that reads as a toolbar above Charges. The card head is the toolbar row. */
        .estimate-billing-manage .ant-card.marginTop10 {
          margin-top: 4px;
          border-radius: 6px;
          box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
        }
        .estimate-billing-manage .ant-card-head {
          min-height: 0;
          padding: 10px 12px 12px;
          background: #fafbfc;
          border-bottom: 1px solid #eef0f3;
        }
        .estimate-billing-manage .ant-card-head-title { padding: 0; }
        /* Let the LoS selectors + action buttons wrap neatly on 2-location orders
           (PICKUP + DELIVERY LoS) instead of colliding on one line. */
        .estimate-billing-manage .ant-card-head-title > .ant-row-flex,
        .estimate-billing-manage .ant-card-head-title > .ant-row {
          flex-wrap: wrap;
          row-gap: 14px;
        }
        /* When the action buttons wrap below the LoS selectors, keep a little
           gap between each button so the row doesn't look cramped. */
        .estimate-billing-manage .ant-card-head-title .ant-btn { margin-bottom: 4px; }
      `}</style>
      {generating ? (
        <Spin spinning delay={200} tip="Generating estimate...">
          <div style={{ minHeight: 300 }} />
        </Spin>
      ) : (
        <FormInvoice
          activeRecordId={orderId}
          currentOrderGroupType={orderGroupType}
          currentBillingTemplateMethod={templateMethod}
          closeModal={() => {}}
          onUnmount={() => {}}
          handleIsLosChanged={props.onLosChanged || (() => {})}
          handleSuccess={() => onRefresh(orderId)}
          getInvoices={() => onRefresh(orderId)}
          filter={props.filter}
          fromEstimate={true}
          estimateIsForInvoiceQuote={isForInvoiceQuote}
        />
      )}
    </div>
  );
};

export default EstimateBillingDetails;
